import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { ONE_QUANTITY } from '@/data/constants/saveForLaterList';
import { ProductType } from '@/data/types/Product';
import { getProductPrice } from 'presentation/events/utils/productUtils';
import {
	CDN_BASE_URL,
	HD_BASE_URL,
	PRODUCT_MEASURE_M2,
	PRODUCT_SKU_STRING_PART,
} from 'shared/custom/constants/commonConstants';

type ProductWithInventories = ProductType & {
	[key: `inventories.${string}.quantity`]: string | number | null | undefined;
};

const hasStock = (product: ProductWithInventories, marketId: string) => {
	try {
		const key = `inventories.M${marketId}.quantity` as const;
		const rawValue = product[key];

		if (rawValue === undefined || rawValue === null) return false;

		const quantity = Number(rawValue);

		if (!Number.isFinite(quantity)) return false;

		return quantity > 0;
	} catch (err) {
		console.error('[PDP-METADATA] error parsing stock', err);
		return false;
	}
};

type GetPDPMetadataParams = {
	product: ProductType;
	marketId: string;
	physicalStoreId: string;
};

export const getPDPMetaData = ({ product, marketId, physicalStoreId }: GetPDPMetadataParams) => {
	if (!product) return null;

	const productURL = HD_BASE_URL + product.seo?.href;
	const partNumber = product.partNumber?.replace(PRODUCT_SKU_STRING_PART, '');

	const { price } = getProductPrice({
		product,
		physicalStoreId,
		quantity: ONE_QUANTITY,
	});

	return [
		{
			'@context': 'https://schema.org/',
			'@type': 'Product',
			name: product.name,
			'@id': productURL,
			image: [CDN_BASE_URL + product.fullImage],
			description: product.shortDescription,
			sku: partNumber,
			mpn: partNumber,
			brand: {
				'@type': 'Thing',
				name: product.manufacturer ?? '',
			},
			offers: {
				'@type': 'Offer',
				url: productURL,
				priceCurrency: DEFAULT_CURRENCY,
				price,
				price_sqm:
					product['x_measurements.quantityMeasure'] === PRODUCT_MEASURE_M2
						? parseFloat(product.price?.[1]?.field1?.OMP ?? '').toFixed(2)
						: '',
				priceValidUntil: '',
				availability: hasStock(product, marketId)
					? 'https://schema.org/InStock'
					: 'https://schema.org/OutOfStock',
			},
		},
	];
};
